class ShippingCalculator extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.countrySelect = this.querySelector('[data-shipping-country]');
    this.provinceSelect = this.querySelector('[data-shipping-province]');
    this.provinceContainer = this.querySelector('[data-province-container]');
    this.zipInput = this.querySelector('[data-shipping-zip]');
    this.calculateButton = this.querySelector('[data-calculate-shipping]');
    this.resultsContainer = this.querySelector('[data-shipping-results]');
    this.spinnerContainer = this.querySelector('[data-shipping-spinner]');
    this.errorContainer = this.querySelector('[data-shipping-error]');

    if (!this.countrySelect || !this.calculateButton) return;

    this.initCountryProvinceSelector();
    this.calculateButton.addEventListener('click', this.onCalculate.bind(this));
    this.countrySelect.addEventListener('change', () => {
      this.clearResults();
      this.saveState();
    });
    this.zipInput.addEventListener('input', () => this.saveState());

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.clearResults();
    });

    this.restoreState();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initCountryProvinceSelector() {
    this.updateProvinces();
    this.countrySelect.addEventListener('change', () => this.updateProvinces());

    const defaultCountry = this.countrySelect.getAttribute('data-default');
    if (defaultCountry) {
      for (let i = 0; i < this.countrySelect.options.length; i++) {
        if (this.countrySelect.options[i].value === defaultCountry) {
          this.countrySelect.selectedIndex = i;
          this.updateProvinces();
          break;
        }
      }
    }
  }

  updateProvinces() {
    const selected = this.countrySelect.options[this.countrySelect.selectedIndex];
    if (!selected) return;

    const raw = selected.getAttribute('data-provinces');
    const provinces = JSON.parse(raw || '[]');

    while (this.provinceSelect.firstChild) {
      this.provinceSelect.removeChild(this.provinceSelect.firstChild);
    }

    if (provinces && provinces.length > 0) {
      provinces.forEach(([code, name]) => {
        const opt = document.createElement('option');
        opt.value = code;
        opt.textContent = name;
        this.provinceSelect.appendChild(opt);
      });
      this.provinceContainer.hidden = false;
    } else {
      this.provinceContainer.hidden = true;
    }
  }

  async onCalculate(event) {
    event.preventDefault();

    const country = this.countrySelect.value;
    const province = this.provinceSelect.value || '';
    const zip = this.zipInput.value.trim();

    if (!country) return;

    this.showLoading();
    this.clearResults();

    try {
      const rates = await this.fetchShippingRates(country, province, zip);
      this.renderResults(rates);
    } catch (error) {
      this.renderError(error.message);
    } finally {
      this.hideLoading();
    }
  }

  async fetchShippingRates(country, province, zip) {
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': country,
      'shipping_address[province]': province,
    });

    const response = await fetch(`/cart/shipping_rates.json?${params.toString()}`);

    if (!response.ok) {
      let message = this.getAttribute('data-error-message') || 'Could not retrieve shipping rates.';
      try {
        const errorData = await response.json();
        if (errorData.errors) {
          const errorMessages = Object.values(errorData.errors).flat();
          if (errorMessages.length) message = errorMessages.join(', ');
        }
      } catch (e) {
        // Use default message
      }
      throw new Error(message);
    }

    const data = await response.json();
    return data.shipping_rates;
  }

  renderResults(rates) {
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.hidden = false;
    this.errorContainer.hidden = true;

    if (!rates || rates.length === 0) {
      this.resultsContainer.innerHTML = `<p class="shipping-calculator__no-rates">${this.getAttribute('data-no-rates-message') || 'No rates available.'}</p>`;
      return;
    }

    const freeLabel = this.getAttribute('data-free-label') || 'Free';

    rates.forEach((rate) => {
      const price = parseFloat(rate.price);
      const isFree = price === 0;
      const rateEl = document.createElement('div');
      rateEl.className = `shipping-calculator__rate${isFree ? ' shipping-calculator__rate--free' : ''}`;
      rateEl.innerHTML = `
        <span class="shipping-calculator__rate-name">${rate.name}</span>
        <span class="shipping-calculator__rate-price">${isFree ? freeLabel : rate.currency + ' ' + price.toFixed(2).replace('.', ',')}</span>
      `;
      this.resultsContainer.appendChild(rateEl);
    });
  }

  renderError(message) {
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
    this.resultsContainer.hidden = true;
  }

  clearResults() {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = '';
      this.resultsContainer.hidden = true;
    }
    if (this.errorContainer) {
      this.errorContainer.textContent = '';
      this.errorContainer.hidden = true;
    }
  }

  showLoading() {
    if (this.spinnerContainer) this.spinnerContainer.classList.add('active');
    if (this.calculateButton) this.calculateButton.disabled = true;
  }

  hideLoading() {
    if (this.spinnerContainer) this.spinnerContainer.classList.remove('active');
    if (this.calculateButton) this.calculateButton.disabled = false;
  }

  saveState() {
    try {
      const state = {
        country: this.countrySelect.value,
        province: this.provinceSelect.value,
        zip: this.zipInput.value,
      };
      sessionStorage.setItem('shipping-calculator-state', JSON.stringify(state));
    } catch (e) {
      // sessionStorage not available
    }
  }

  restoreState() {
    try {
      const saved = sessionStorage.getItem('shipping-calculator-state');
      if (!saved) return;

      const { country, province, zip } = JSON.parse(saved);

      if (country) {
        for (let i = 0; i < this.countrySelect.options.length; i++) {
          if (this.countrySelect.options[i].value === country) {
            this.countrySelect.selectedIndex = i;
            this.updateProvinces();
            break;
          }
        }
      }

      if (province) {
        for (let i = 0; i < this.provinceSelect.options.length; i++) {
          if (this.provinceSelect.options[i].value === province) {
            this.provinceSelect.selectedIndex = i;
            break;
          }
        }
      }

      if (zip) {
        this.zipInput.value = zip;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
}

customElements.define('shipping-calculator', ShippingCalculator);
